import { useEffect, useRef, useState } from 'react';

/*
 * The Musixmatch impression (ENG-415), shared by every surface that shows a
 * provider lyrics body: the performer's stand on the streamer page and the
 * sing-along panel on the watch page.
 *
 * Loading `script_tracking_url` is how Musixmatch counts a lyrics view, and
 * counting it is the condition the words are served to us on — so it is not
 * the sort of thing to keep two copies of. It lives here so that both callers
 * fire it the same way, exactly once per view, against the same host check.
 */

/**
 * The tracking URL is a third-party script address taken straight out of an
 * API response and appended to this document, where it would run with the
 * page's origin and the session in `sessionStorage`. There is no CSP on these
 * pages to catch a bad one, so the only thing between a compromised or
 * mistaken payload and arbitrary script execution is this check: the
 * provider's own hosts, over TLS, and nothing else.
 */
export const isProviderTrackingUrl = (url) => {
  try {
    const { protocol, hostname } = new URL(url);
    return (
      protocol === 'https:' &&
      (hostname === 'musixmatch.com' || hostname.endsWith('.musixmatch.com'))
    );
  } catch {
    return false;
  }
};

/*
 * ## What counts as one view
 *
 * "Once per lyrics view" is not "once per URL for the life of the page", and
 * the difference is the direction that creates exposure. A set list that comes
 * back round to a song — a replay, a restart after a skip, an encore — puts
 * the same words in front of a reader a second time, and `useSongLyrics`
 * answers it from its page cache with the same `script_tracking_url`. Keyed on
 * the URL alone that second view goes uncounted, which is the licence
 * condition quietly failing while the words are on screen.
 *
 * So a view is tracked by who is looking at it: it begins when the first
 * reader has the words on screen and ends when the last one stops. Both
 * callers unmount and remount around the words they show — React re-parenting
 * the stand through fullscreen, StrictMode double-invoking an effect, a
 * subtree suspending — and none of those is a reader looking away. Every one
 * of them puts the remount in the *same task* as the unmount, so an ended view
 * is held to the end of the task before it is forgotten: an artefact of
 * rendering rejoins the view it never really left, while anything a person did
 * (closing the panel, the set coming back round to a song) lands in a later
 * task and is the new view it actually is.
 *
 * Module scope rather than component state for exactly that reason: state does
 * not survive the unmount this is here to see through.
 */

/** url → { viewers, reported, release } for views live or lately ended. */
const views = new Map();

/** Reset between tests; the page itself never needs this. */
export const resetLyricsImpressions = () => {
  for (const view of views.values()) {
    if (view.release) clearTimeout(view.release);
  }
  views.clear();
};

/** Opens (or rejoins) the view for `url`, returning its record. */
const openView = (url) => {
  const existing = views.get(url);
  if (existing) {
    existing.viewers += 1;
    if (existing.release) {
      clearTimeout(existing.release);
      existing.release = null;
    }
    return existing;
  }
  const view = { viewers: 1, reported: false, release: null };
  views.set(url, view);
  return view;
};

/** The reader has stopped looking; the view ends unless they come straight back. */
const closeView = (url) => {
  const view = views.get(url);
  if (!view) return;
  view.viewers -= 1;
  if (view.viewers > 0 || view.release) return;
  view.release = setTimeout(() => {
    // Checked again on the way out: a remount inside the same task cancelled
    // this timer, but a race is cheaper to re-check than to reason about.
    const current = views.get(url);
    if (current && current.viewers <= 0) views.delete(url);
  }, 0);
};

/**
 * Reports one impression for `trackingUrl`, once, while `visible`.
 *
 * Two steps rather than one effect, so that it fires exactly once per lyrics
 * view:
 *
 *   1. a view begins when a body is on screen — not merely fetched. A reader
 *      who collapsed the panel is not reading lyrics, and counting them would
 *      claim an impression we did not show.
 *   2. the tag is mounted for that URL and stays mounted until the URL
 *      changes, so hiding and re-showing the same song is the same view. A
 *      song change brings a new URL, and the same song coming back round
 *      later brings a new view of the old one.
 *
 * `pixelUrl` is the provider's own no-JavaScript alternative to the script and
 * is used only when the script fails to load, which on these pages mostly
 * means an ad blocker ate it: firing both would report two impressions for one
 * view.
 */
export const useLyricsImpression = (trackingUrl, { pixelUrl = null, visible = true } = {}) => {
  // Held in a ref rather than in the tracking effect's dependencies: the
  // fallback is read long after that effect ran, and re-running it for a
  // changed pixel URL would tear down a script tag that is mid-flight.
  const pixelRef = useRef(pixelUrl);
  useEffect(() => {
    pixelRef.current = pixelUrl;
  }, [pixelUrl]);

  const [viewedUrl, setViewedUrl] = useState(null);
  useEffect(() => {
    setViewedUrl((current) => {
      if (!trackingUrl) return null;
      // The view already under way continues across a hide and a reopen.
      if (current === trackingUrl) return current;
      // A different song's URL: its view only starts once it is on screen,
      // and the previous song's tag goes with it either way.
      return visible ? trackingUrl : null;
    });
  }, [trackingUrl, visible]);

  useEffect(() => {
    if (!viewedUrl) return undefined;
    const view = openView(viewedUrl);
    if (view.reported) return () => closeView(viewedUrl);

    // Set before the host check so a payload pointing somewhere unexpected is
    // refused once per view rather than on every re-render of it.
    view.reported = true;
    if (!isProviderTrackingUrl(viewedUrl)) {
      // Not the provider's host: the impression goes uncounted rather than
      // this page running whatever the payload pointed at.
      console.warn('[lyrics] refusing to load a tracking script from an unexpected host');
      return () => closeView(viewedUrl);
    }

    const script = document.createElement('script');
    script.src = viewedUrl;
    script.async = true;
    script.dataset.streamLyricsTracking = 'true';
    // Ad blockers routinely drop tracking.musixmatch.com, and an impression
    // that silently goes uncounted is the licence condition quietly failing.
    // The pixel is the provider's own fallback for exactly this, and using it
    // here reports one impression rather than two.
    script.addEventListener('error', () => {
      const pixel = pixelRef.current;
      if (!pixel || !isProviderTrackingUrl(pixel)) {
        console.warn('[lyrics] the Musixmatch impression could not be reported');
        return;
      }
      const image = document.createElement('img');
      image.src = pixel;
      image.alt = '';
      image.width = 1;
      image.height = 1;
      image.setAttribute('aria-hidden', 'true');
      image.dataset.streamLyricsTrackingPixel = 'true';
      image.style.display = 'none';
      document.body.appendChild(image);
    });
    document.body.appendChild(script);
    // Tidies the document on the way out; a script that already ran cannot
    // be un-run, so removing it never un-counts anything.
    return () => {
      closeView(viewedUrl);
      script.remove();
    };
  }, [viewedUrl]);
};

export default useLyricsImpression;
